import { Component, Input, OnInit, OnChanges, NgZone } from '@angular/core';
import { Subscription }                                from 'rxjs';
import { ChartConfig }                                 from '../chart';
import { ChartHelper }                                 from '../chart-helper';

export interface Rectangle {
  x: number,
  y: number,
  width: number,
  height: number,
  text: string,
  fill: string
}

@Component({
  standalone: false,
  selector:    '[cs-bar-chart]',
  templateUrl: './bar-chart.component.html'

})
export class CsBarChartComponent implements OnInit, OnChanges {

  @Input() config: ChartConfig;

  maxValue: number;
  minValue = 0;   // lower bound of the value axis (negative when data dips below zero)
  rotateXLabels = false;
  yStep: number;
  xStep: number;
  yFactor: number;
  xOffsets          = 50; // for the Y-axis labels
  yOffsets          = 35; // for the X-axis labels
  firstBarOffset    = 10;
  _hoveredIndex     = -1;
  hoveredInnerIndex = -1;
  bars: Rectangle[][];
  texts             = [];
  lines             = [];
  axisConfig;
  labels;

  // animation intro.
  private enableAnimation       = false;
  private animationSubscribtion: Subscription;
  private animated;
  private currentAnimationStep  = 1;
  private animationStep         = 0.1;
  private animatioFrameInterval = 40;
  private hoveredText: any      = {};

  get hoveredIndex() {
    return this._hoveredIndex;
  }

  set hoveredIndex(i) {
    this._hoveredIndex = i;
    this.hoveredText   = i === -1 ? {} : this.getTooltip(i);
  }

  constructor(public ngZone: NgZone) {
  }

  private isDataReady() {
    return this.config && this.config.data && this.config.data.datasets && this.config.data.datasets.length;
  }

  ngOnInit() {
    setTimeout(() => {
      if (this.isDataReady()) {
        this.initMaxValue();
        this.calculateBars();
      }
    }, 1);

  }

  ngOnChanges(changes: any) {
    if (changes.hasOwnProperty('config') && this.isDataReady()) {
      // this.config = changes['config'].currentValue;
      this.initMaxValue();
      this.calculateBars();
    }
  }

  initMaxValue() {

    this.currentAnimationStep = this.enableAnimation ? 0 : 1;
    if (this.config.isStacked) {
      const dataSegments = this.config.data.datasets
                               .map((d) => d.data);
      let maxSum         = 0;
      for (let j = 0; j < dataSegments[0].length; j++) {
        let sum = 0;
        for (let i = 0; i < dataSegments.length; i++) {
          sum += dataSegments[i][j];
        }
        maxSum = Math.max(maxSum, sum);
      }
      // arr = values[0].map((_, i) => values.map((a)=>a[i]).reduce((a,b)=>a+b)) // shortcut
      this.maxValue = ChartHelper.getMaxStepValue(maxSum, this.config.ySteps);
    } else {
      const values  = this.config.data.datasets
                          .map((d) => d.data)
                          .reduce((a, b) => a.concat(b), []);
      const rawMax  = Math.max(Math.max.apply(this, values), 0);
      const rawMin  = Math.min(Math.min.apply(this, values), 0);
      if (rawMin < 0) {
        // Negatives present: one round step, min & max snapped to multiples of it, so bars can
        // hang below a real zero baseline instead of silently not drawing.
        const magnitude = Math.max(Math.abs(rawMax), Math.abs(rawMin), 1);
        this.yStep      = ChartHelper.getMaxStepValue(magnitude, this.config.ySteps) / this.config.ySteps;
        this.maxValue   = Math.ceil(rawMax / this.yStep) * this.yStep;
        this.minValue   = Math.floor(rawMin / this.yStep) * this.yStep;
      } else {
        this.maxValue = ChartHelper.getMaxStepValue(rawMax, this.config.ySteps);
        this.minValue = 0;
      }
    }
    if (this.maxValue === this.minValue) {
      this.maxValue = this.minValue + 1;
    }
  }

  initAxisConfig() {
    this.axisConfig        = {chartWidth: this.config.chartWidth};
    this.axisConfig.s      = {
      x: Math.round(this.config.xPadding + this.xOffsets) + 0.5,
      y: Math.round(this.config.yPadding - this.yOffsets) + 0.5
    };
    this.axisConfig.e      = {
      // Right inset mirrors the left one so the plot is symmetric in the bubble.
      x: Math.round(this.config.chartWidth - this.config.xPadding - this.xOffsets) + 0.5,
      y: Math.round(this.config.chartHeight - this.config.yPadding - this.yOffsets) + 0.5
    };
    this.axisConfig.ySteps = [];
    this.axisConfig.xSteps = [];
    const stepCount = Math.max(1, Math.round((this.maxValue - this.minValue) / this.yStep));
    // Positive-only charts start at i=1 (unchanged); negative-aware charts also draw the 0 tick.
    for (let i = (this.minValue < 0 ? 0 : 1); i <= stepCount; i++) {
      const value = this.minValue + i * this.yStep;
      const step  = {
        text:   this.config.yLabelFormatter(ChartHelper.getValueFormatted(Math.round(value))),
        // The zero gridline is the visual reference when bars hang below it — drawn stronger.
        isZero: this.minValue < 0 && value === 0,
        y:      Math.round(this.config.chartHeight - this.config.yPadding - this.yOffsets - (value - this.minValue) * this.yFactor) + 0.5
      };
      this.axisConfig.ySteps.push(step);
    }
    const labels = this.config.data.labels;
    // When the per-category slot is too narrow for horizontal text, rotate the ticks and
    // truncate long labels (full text stays available as a hover <title>).
    const rotate   = this.rotateXLabels;
    // Vertical labels live in the fixed band below the axis: 13 chars at 11px (~75px of
    // text + 8px gap) fits the 105px band with margin — longer would clip at the bottom.
    const maxChars = rotate ? 13 : Math.max(4, Math.floor(this.xStep / 7));
    // Center each tick under its drawn bar group, mirroring calculateBars' slot width:
    // the bars of one category span (xStep - slot) px, with `slot` left as the gap.
    const groups       = Array.from(new Set(this.config.data.datasets.map((a) => a.group)));
    const slot         = this.config.isStacked
      ? this.xStep / (groups.length + 1)
      : this.xStep / (groups.length * this.config.data.datasets.length + 1);
    const centerOffset = (this.xStep - slot) / 2;
    for (let i = 0, l = labels.length; i < l; i++) {
      const full = labels[i] == null ? '' : String(labels[i]);
      const step = {
        text:     full.length > maxChars ? full.slice(0, maxChars - 1).trimEnd() + '…' : full,
        fullText: full,
        rotate,
        x:        Math.round(this.config.xPadding + this.xOffsets + this.firstBarOffset + i * this.xStep + centerOffset) + 0.5
      };
      this.axisConfig.xSteps.push(step);
    }
  }

  initSteps() {
    if (this.minValue >= 0) {
      this.yStep = this.maxValue / this.config.ySteps;   // negative case sets yStep in initMaxValue
    }
    const n = this.config.data.datasets[0].data.length;
    this.xStep = (this.config.chartWidth - this.config.xPadding * 2 - this.xOffsets) / n;
    // Dense charts (narrow slots) auto-switch to vertical 90° labels in a fixed-height band
    // below the axis. Vertical labels stay within their own column, so no extra left offset.
    this.rotateXLabels = this.xStep < 90;
    this.yOffsets      = this.rotateXLabels ? 105 : 35;
    this.xOffsets      = 50;
    // Symmetric: the plot is inset by xOffsets on BOTH sides.
    this.xStep         = (this.config.chartWidth - this.config.xPadding * 2 - this.xOffsets * 2) / n;
    this.yFactor = (this.config.chartHeight - this.config.yPadding * 2 - this.yOffsets) / (this.maxValue - this.minValue);
    this.initAxisConfig();
  }

  initData() {
    const data: any    = [];
    const labelsLevel1 = this.config.data.labels; // i => labels.length
    const labelsLevel2 = this.config.data.datasets.map(d => { // j => datasets.length
      return d.label;
    });
    for (let j = 0; j < this.config.data.datasets.length; j++) {
      const groupLevel1 = [];
      const ds          = this.config.data.datasets[j];
      if (!ds.group) {
        ds['_group'] = this.config.isStacked ? '' : ds.label + j;
      }
    }

  }

  calculate() {
    if (!this.config.chartWidth || !this.config.chartHeight) {
      return;
    }

  }

  calculateBars() {
    if (!this.config.chartWidth || !this.config.chartHeight) {
      return;
    }
    this.initSteps();
    this.labels = [];
    const bars  = [];
    for (let i = 0; i < this.config.data.datasets[0].data.length; i++) {
      const values              = this.config.data.datasets.map((a) => a.data[i]);
      const groupStack          = this.config.data.datasets.reduce((gs: any, val) => {
        if (!gs[val.group]) {
          gs[val.group] = [];
        }
        gs[val.group].push({
          label:    (val.label || '') + (val.label && val.group ? ' - ' : '') + (val.group || '')
          , values: val.data[i]
        });
        return gs;
      }, {});
      const groups              = Array.from(new Set(this.config.data.datasets.map((a) => a.group)));
      let x                     = this.config.xPadding + this.xOffsets + i * this.xStep + this.firstBarOffset, y = this.getTranslatedY(0);
      // tslint:disable-next-line:max-line-length
      const width               = this.currentAnimationStep * (this.config.isStacked ? this.xStep / (groups.length + 1) : this.xStep / (groups.length * values.length + 1));
      const bars_i: Rectangle[] = [];
      let gi                    = 0;
      Object.keys(groupStack).forEach(g => {
        for (let j = 0; j < groupStack[g].length; j++) {
          const raw = this.currentAnimationStep * this.getFactoredValue(groupStack[g][j].values);
          let rectY: number, rectH: number;
          if (this.config.isStacked) {
            // Stacked keeps the original accumulate-upward behaviour.
            y -= raw;
            rectY = y;
            rectH = raw;
          } else if (raw >= 0) {
            rectY = y - raw;
            rectH = raw;
          } else {
            // Negative value: bar hangs below the zero baseline (y is at zero here).
            rectY = y;
            rectH = -raw;
          }
          const color   = this.config.isStacked || this.config.data.datasets.length > 1
            ? this.config.colors[(j + gi * groupStack[g].length) % this.config.colors.length]
            : this.config.colors[i % this.config.colors.length];
          const hovered = this.hoveredIndex === i;
          // tslint:disable-next-line:max-line-length
          // 2px breathing room between sibling bars of one category (drawn width only —
          // slot advance below is untouched, so positions stay identical).
          const drawnWidth = Math.max(1, Math.round(width) - (this.config.data.datasets.length > 1 ? 2 : 0));
          bars_i.push({
            x:      Math.round(x),
            y:      Math.round(rectY),
            width:  drawnWidth,
            height: Math.round(rectH),
            text:   ChartHelper.getValueLocale(groupStack[g][j].values),
            fill:   color
          });
          if (!this.config.isStacked) {
            x += width;
            y = this.getTranslatedY(0);
          }
          this.labels[(j + gi * groupStack[g].length)] = groupStack[g][j].label;
        }
        gi++;
        x += width;
        y = this.getTranslatedY(0);
      });
      bars.push(bars_i);
      this.texts[i] = this.getTooltip(i);
    }
    this.ngZone.run(() => {
      this.bars = bars;
    });
    this.currentAnimationStep += this.animationStep;
    if (this.currentAnimationStep < 1) {
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(_ => this.calculateBars());
      });
    }
  }

  private getTranslatedY(value: number) {
    return this.config.chartHeight - this.config.yPadding - this.yOffsets - this.getFactoredValue(value - this.minValue);
  }

  private getFactoredValue(value: number) {
    return value * this.yFactor;
  }

  private getLabelsAndValues(i) {
    const values = this.config.data.datasets.map((a) => (a.label ? (a.label + ':') : '') + a.data[i]);
    return [this.config.data.labels[i] || ''].concat(values);
  }

  private getTooltip(hoveredIndex) {
    const text     = this.getLabelsAndValues(hoveredIndex);
    const align    = hoveredIndex > this.config.data.labels.length / 2 ? 'left' : 'right';
    const tooltipX = this.config.xPadding + this.xOffsets + this.firstBarOffset + hoveredIndex * this.xStep + (align === 'left'
      ? 0
      : this.xStep / 2);
    const min      = Math.min.apply([], this.config.data.datasets.map((a) => a.data[hoveredIndex]));
    const tooltipY = this.getTranslatedY(min);
    const colors   = this.config.isStacked || this.config.data.datasets.length > 1
      ? ['white', ...this.config.colors]
      : ['white', this.config.colors[hoveredIndex % this.config.colors.length]];
    return {text: text, x: tooltipX, y: tooltipY, align: align};
  }
}
